import _ from 'underscore'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import Checkbox from 'components/Checkbox'
import CollapsibleCard from 'components/CollapsibleCard'
import CustomerGroup from 'pages/shared/NotificationRecipientForm/CustomersCard/CustomerGroup'
import TextInput from 'components/TextInput'

import { customerDisplayName } from 'helpers/formatters'

import useScreenSize from 'hooks/useScreenSize'

import { CustomerBase } from 'types/Customer'
import { NotificationRecipient } from 'types/NotificationRecipient'

type Props = {
  customers: Pick<CustomerBase, 'id' | 'firstName' | 'lastName' | 'companyName'>[]
  recipient: Pick<NotificationRecipient, 'customerIds'>
  collapsedByDefault: boolean
  onChangeCustomerIds: (arg0: number[]) => void
}

const CustomersCard = ({ customers, recipient, collapsedByDefault, onChangeCustomerIds }: Props) => {
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerSearchRegex, setCustomerSearchRegex] = useState('')

  const screenSize = useScreenSize()

  const allCustomerIds = useMemo(() => _.map(customers, ({ id }) => Number(id)), [customers])

  const getAllCustomerCheckBoxState = useCallback(() => {
    if (allCustomerIds.length === recipient.customerIds?.length) return true
    else if (recipient.customerIds?.length === 0) return false
    else return 'indeterminate'
  }, [allCustomerIds, recipient.customerIds])

  const customersGroupedByFirstLetterOfName = useMemo(() => {
    return _.chain(customers)
      .sortBy((customer) => customerDisplayName(customer.firstName, customer.lastName, customer.companyName).toLowerCase())
      .groupBy((customer) => {
        const firstChar = customerDisplayName(customer.firstName, customer.lastName, customer.companyName).toUpperCase()[0] || ''

        return /^[A-Za-z]+$/.test(firstChar) ? firstChar : '#'
      }).value()
  }, [customers])

  const groupLetters = useMemo(() => {
    const keys = Object.keys(customersGroupedByFirstLetterOfName)

    if (keys[0] === '#') {
      const firstKey = keys.shift()
      if (firstKey) keys.push(firstKey)
    }

    return keys
  }, [customersGroupedByFirstLetterOfName])

  return (
    <CollapsibleCard
      title={`For Customers (${recipient.customerIds?.length})`}
      collapsedByDefault={collapsedByDefault}
    >
      <S.TextInput
        label={''}
        onChange={event => {
          const value = event.target.value
          const regexValue = value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
          setCustomerSearch(value)
          setCustomerSearchRegex(regexValue)
        }}
        value={customerSearch}
        placeholder="Type here to find customers..."
        aria-label="find customer"
        onClear={() => {
          setCustomerSearch('')
          setCustomerSearchRegex('')
        }}
      />

      <Checkbox
        checked={getAllCustomerCheckBoxState()}
        label={'Select All Customers'}
        onChange={(e) => onChangeCustomerIds(e.target.checked ? allCustomerIds : [])}
      />

      <S.CustomersCard>
        {(() => {
          const groups = groupLetters.map((letter) => ({
            component: (
              <CustomerGroup
                {...{ letter, customerSearch, customerSearchRegex }}
                key={letter}
                customers={customersGroupedByFirstLetterOfName[letter]}
                selectedIds={recipient.customerIds || []}
                onSelectedIdsChange={(customerIds) => onChangeCustomerIds(customerIds)}
              />
            ),
            itemCount: customersGroupedByFirstLetterOfName[letter].length + 2 // 2 to account for letter and space prior to it
          }))

          const totalItems = customers.length + groupLetters.length * 2 // multiply by 2 to include space before letter
          if (screenSize === 'small' || totalItems <= 20) {
            return _.map(groups, (group) => group.component)
          } else {
            const itemsPerColumn = Math.ceil(totalItems / 4)

            const columns: React.ReactNode[] = []
            for (let i = 0; i <= 3 && groups.length > 0; i++) {
              columns.push(
                <S.Column key={`CustomerSelectionColumn-${i}`} data-testid="column">
                  {(() => {
                    const groupsForColumn: React.ReactNode[] = []
                    let numberOfItemsInColumn = 0

                    do {
                      let group = groups.shift()

                      const spaceLeftInColumn = itemsPerColumn - numberOfItemsInColumn
                      const letter = group?.component?.key?.split('-')[0] || ''

                      if (i < 3 && customersGroupedByFirstLetterOfName[letter]?.length > spaceLeftInColumn) {
                        const groupThatGoesInNextColumn = {
                          component: <CustomerGroup
                                    {...{ letter, customerSearch, customerSearchRegex }}
                                    key={letter + '-second-section'}
                                    customers={customersGroupedByFirstLetterOfName[letter]?.slice(spaceLeftInColumn, customersGroupedByFirstLetterOfName[letter]?.length)}
                                    selectedIds={recipient.customerIds || []}
                                    onSelectedIdsChange={(customerIds) => onChangeCustomerIds(customerIds)}
                                     />,
                          itemCount: customersGroupedByFirstLetterOfName[letter]?.length + 2 - spaceLeftInColumn
                        }

                        groups.unshift(groupThatGoesInNextColumn)

                        group = {
                          component: <CustomerGroup
                                    {...{ letter, customerSearch, customerSearchRegex }}
                                    key={letter + '-first-section'}
                                    customers={customersGroupedByFirstLetterOfName[letter]?.slice(0, spaceLeftInColumn)}
                                    selectedIds={recipient.customerIds || []}
                                    onSelectedIdsChange={(customerIds) => onChangeCustomerIds(customerIds)}
                                     />,
                          itemCount: spaceLeftInColumn + 2
                        }
                      }

                      numberOfItemsInColumn += group?.itemCount || 0

                      groupsForColumn.push(group?.component)
                    } while ((i < 3 && numberOfItemsInColumn < itemsPerColumn) || (i === 3 && groups.length > 0)) // eslint-disable-line no-unmodified-loop-condition

                    return groupsForColumn
                  })()}
                </S.Column>
              )
            }

            return columns
          }
        })()}
      </S.CustomersCard>
    </CollapsibleCard>
  )
}

export default CustomersCard

const S = {
  CustomersCard: styled.div`
    margin-top: 10px;
  `,
  Column: styled.div`
    width: 25%;
    display: inline-block;
    vertical-align: top;
    overflow: hidden;
  `,
  TextInput: styled(TextInput)`
    display: block;
    width: 100%;
    max-width: 800px;
  `
}
